// The Price map's rows, worked out from the hub's own price map and catalogue and from what Stripe
// says each price costs (AdminPrices.dc.html; docs/data-model.md 4.3 and 10.4). Pure: no network and
// no React, so the rules are checked on their own (tests/e2e/admin-prices.spec.js).
//
// 1 row per Stripe price in hub.price_map (Adam, 4 Oct: "1 row per Stripe price"), then a Waiting
// row for each paid product with no price yet, and a By hand row for each private client space.
// Nothing here stores an amount: amounts come from stripe-prices' owner and admin part each time.
import { uk, time, shortDate } from '../../lib/format.js'
import { ukToIso, ukDayOf, ukDateShort, labelFromStripeName } from '../ui/format.js'
import { MISMATCH_WORDS, doneStamp } from '../mismatches.js'

// The words the board draws, and the ones it implies
export const WORDS = {
  eyebrow: 'Stripe sells, the hub opens',
  title: 'Price map',
  lede: 'Every Stripe price, and what it opens when someone pays.',
  footer: "Free products have no price, so they're not on this map. Every account sees them from the moment you switch them on.",
  nothing: 'Nothing',
  placeRule: 'Holds the place. Nothing opens until the balance is paid',
  subscriptionRule: "While it's paid. A failed payment shows a banner for 7 days, then pauses it.",
  waitingPrice: 'Price not set',
  waitingFrom: 'Not on sale yet',
  byHandFrom: 'Not linked yet',
  byHandOpens: 'A private client space for that client',
  byHandRule: 'You add each client by hand. Their space stays open until you end it',
  byHandPrice: 'Set by hand',
  notSet: 'Not set',
  notRead: 'Amount not read',
  notFound: 'Not found in Stripe',
  archived: 'Archived in Stripe',
  unknownRule: 'Logged when it sells, and nothing opens',
}

export const STATUS_WORDS = { mapped: 'Mapped', not_in_hub: 'Not in the hub', waiting: 'Waiting', by_hand: 'By hand' }

const INTERVALS = { day: 'a day', week: 'a week', month: 'a month', year: 'a year' }

// "For as long as the course runs, and at least 12 months." -> the board writes these lines without
// the closing full stop, so every line in the column reads the same
export function trimStop(s) {
  return String(s || '').trim().replace(/\.$/, '')
}

// Where a product not moved in lives now (src/config/products.js's notMovedIn, and CLAUDE.md's
// "Products that haven't moved in yet"), kept here so this file needs no build settings
const OLD_SITES = { '/poyt': 'course.adamslee.co.uk', '/accelerator': 'members.adamslee.co.uk' }
export function oldSiteOf(product) {
  return OLD_SITES[product?.path] || null
}

// The product a price not in the hub sells, if the hub can tell: the row's outside_product_id (stage
// 4, POYT's place links), else a paid product whose name its label starts with ("Profit On Your
// Terms, balance"). Longest name first, so "The Property Accelerator" never matches a shorter one.
export function productForLabel(label, catalogue) {
  const text = String(label || '').toLowerCase()
  const paid = (catalogue || []).filter(c => c.access_kind === 'paid' && c.name)
    .sort((a, b) => b.name.length - a.name.length)
  return paid.find(c => text === c.name.toLowerCase() || text.startsWith(`${c.name.toLowerCase()},`) || text.startsWith(`${c.name.toLowerCase()} `)) || null
}

// "£400+vat to secure a place", "£597+vat balance", "£249+vat a month"
export function priceLine(amount, { kind, interval } = {}) {
  if (!amount) return null
  const bits = [amount]
  if (kind === 'place') bits.push('to secure a place')
  else if (kind === 'balance') bits.push('balance')
  if (interval && INTERVALS[interval]) bits.push(INTERVALS[interval])
  return bits.join(' ')
}

// "Balance due Tue 20 Oct, 7pm", for a place price's default due date (C10)
export function dueLine(price) {
  if (!price) return null
  if (price.default_due_at) return `Balance due ${shortDate(price.default_due_at)}, ${time(price.default_due_at)}`
  if (Number.isInteger(price.default_due_days)) {
    const n = price.default_due_days
    return n === 0 ? 'Balance due the day the place is secured' : `Balance due ${n} ${n === 1 ? 'day' : 'days'} after the place is secured`
  }
  return 'No balance date set yet'
}

// A due date that has already gone: a place secured now would be past due at once
export function duePassed(price, nowMs = Date.now()) {
  return Boolean(price?.default_due_at) && Date.parse(price.default_due_at) < nowMs
}
export const DUE_PASSED = 'That date has passed, so a place secured now is past due at once.'

// The amount the board writes for a price, from stripe-prices: null while it isn't read
function amountOf(amounts, id) {
  if (!amounts) return { amount: null, interval: null, active: true, read: false }
  const a = amounts.prices?.[id]
  if (!a) return { amount: null, interval: null, active: true, read: true, missing: true }
  return { amount: a.display || null, interval: a.interval || null, active: a.active !== false, read: true, productName: a.product_name || null }
}

const KIND_ORDER = { full: 0, place: 1, balance: 2 }

/**
 * Every row of the Price map, in the order the screen draws them.
 *   prices    hub.price_map rows
 *   links     hub.price_map_products rows
 *   balances  hub.price_map_balances rows
 *   catalogue hub.catalogue rows (id, slug, path, name, type, access_kind, state, look, moved_in,
 *             terms_line, sort_order, settings)
 *   amounts   stripe-prices' { prices: { id: { display, interval, product_name, active } } }, or null
 *             when Stripe couldn't be read
 */
export function buildRows({ prices = [], links = [], balances = [], catalogue = [], amounts = null, nowMs = Date.now() }) {
  const byId = new Map(catalogue.map(c => [c.id, c]))
  const priceById = new Map(prices.map(p => [p.stripe_price_id, p]))
  const productsOf = id => links.filter(l => l.stripe_price_id === id).map(l => byId.get(l.product_id)).filter(Boolean)
  const attributed = new Set()

  const rows = prices.map(p => {
    const placeLinks = balances.filter(b => b.balance_price_id === p.stripe_price_id)
    const kind = p.secures_place ? 'place' : placeLinks.length ? 'balance' : 'full'
    const places = placeLinks.map(b => priceById.get(b.place_price_id)).filter(Boolean)
    const opensProducts = kind === 'balance'
      ? [...new Map(places.flatMap(pl => productsOf(pl.stripe_price_id)).map(c => [c.id, c])).values()]
      : productsOf(p.stripe_price_id)
    const inHub = opensProducts.filter(c => c.moved_in)
    const notMovedIn = opensProducts.length > 0 && inHub.length === 0
    const status = p.status === 'not_in_hub' || notMovedIn ? 'not_in_hub' : 'mapped'
    const outside = status === 'not_in_hub'
      ? (byId.get(p.outside_product_id) || (notMovedIn ? opensProducts[0] : null) || productForLabel(p.label, catalogue))
      : null
    const product = status === 'mapped' ? (opensProducts[0] || null) : outside
    if (product) attributed.add(product.id)
    const a = amountOf(amounts, p.stripe_price_id)

    let opens
    let rule
    if (status === 'not_in_hub') {
      if (outside) {
        const site = oldSiteOf(outside)
        opens = `${outside.name}, on its own site until it moves`
        rule = site ? `Nothing opens in the hub. ${site} gives the access` : 'Nothing opens in the hub'
      } else {
        opens = WORDS.nothing
        rule = WORDS.unknownRule
      }
    } else if (kind === 'place') {
      opens = WORDS.nothing
      rule = WORDS.placeRule
    } else {
      opens = inHub.length ? inHub.map(c => c.name).join(', ') : WORDS.nothing
      if (a.interval) rule = trimStop(WORDS.subscriptionRule)
      else if (p.access_months) rule = `For ${p.access_months} ${p.access_months === 1 ? 'month' : 'months'} from payment`
      else rule = trimStop(inHub[0]?.terms_line) || 'Everything, from payment'
      if (p.payments_total) rule = `${rule}. ${p.payments_total} payments, and it opens on payment ${p.opens_on_payment || 1}`
    }

    // a place price: its balance date, and the balance its 1 pay button pays
    let place = null
    if (kind === 'place' && status === 'mapped') {
      const under = balances.filter(b => b.place_price_id === p.stripe_price_id)
      const button = under.find(b => b.pay_button)
      const buttonPrice = button ? priceById.get(button.balance_price_id) : null
      place = {
        due: dueLine(p),
        passed: duePassed(p, nowMs),
        balances: under.map(b => ({ ...b, price: priceById.get(b.balance_price_id) || null })),
        buttonId: button?.balance_price_id || null,
        // the balance's amount as Stripe has it, or nothing when it isn't read (never its label)
        buttonAmount: buttonPrice ? (amountOf(amounts, buttonPrice.stripe_price_id).amount || null) : null,
        buttonLink: buttonPrice?.balance_payment_url || null,
      }
    }

    return {
      key: p.stripe_price_id,
      id: p.stripe_price_id,
      source: 'price',
      kind,
      status,
      statusWord: STATUS_WORDS[status],
      product,
      dotProduct: product,
      price: a.amount ? priceLine(a.amount, { kind, interval: a.interval }) : (a.read ? (a.missing ? WORDS.notFound : WORDS.notRead) : null),
      priceKnown: Boolean(a.amount),
      active: a.active,
      label: p.label,
      from: p.sold_from || null,
      opens,
      rule,
      place,
      row: p,
      order: [
        product ? (product.sort_order ?? 99) + (product.access_kind === 'paid' ? 0 : 100) : 500,
        status === 'mapped' ? 0 : 1,
        KIND_ORDER[kind],
        a.active ? 0 : 1,
      ],
    }
  })

  rows.sort((x, y) => {
    for (let i = 0; i < x.order.length; i++) if (x.order[i] !== y.order[i]) return x.order[i] - y.order[i]
    return String(x.label).localeCompare(String(y.label))
  })

  // a paid product with no price at all yet (Property Cashflow For Life before its launch): Waiting.
  // A product whose prices all sell it on its own site isn't waiting (the Accelerator on staging), and a
  // product not moved in has no price in the hub by design.
  const waiting = catalogue
    .filter(c => c.access_kind === 'paid' && c.type !== 'client_space' && c.moved_in && !attributed.has(c.id))
    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
    .map(c => ({
      key: `waiting:${c.id}`, id: null, source: 'waiting', kind: 'full', status: 'waiting', statusWord: STATUS_WORDS.waiting,
      product: c, dotProduct: c, price: WORDS.waitingPrice, priceKnown: false, active: true, label: c.name,
      from: WORDS.waitingFrom, opens: c.name, rule: trimStop(c.terms_line) || 'Everything, from payment', place: null, row: null,
    }))

  // a private client space: added by hand, with no Stripe link until there's a client (decision 36)
  const byHand = catalogue
    .filter(c => c.type === 'client_space' && c.access_kind === 'paid' && !attributed.has(c.id))
    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
    .map(c => ({
      key: `by_hand:${c.id}`, id: null, source: 'by_hand', kind: 'full', status: 'by_hand', statusWord: STATUS_WORDS.by_hand,
      product: c, dotProduct: c, price: amountFromNote(c.settings?.prices_note) || WORDS.byHandPrice, priceKnown: false, active: true,
      label: c.name, from: WORDS.byHandFrom, opens: WORDS.byHandOpens, rule: WORDS.byHandRule, place: null, row: null,
    }))

  return [...rows, ...waiting, ...byHand]
}

// "£10,000+vat, in full or 50% upfront ..." -> "£10,000+vat": the leading price in a product's
// prices_note, the copy setting AdminProducts shows (no amount is stored anywhere else)
export function amountFromNote(note) {
  const m = /^\s*(£[\d,]+(?:\.\d{2})?(?:\+vat)?)/i.exec(String(note || ''))
  return m ? m[1] : null
}

// ---- A place price's balance date, as the owner's forms hold it --------------------------------

const pad = n => String(n).padStart(2, '0')

// On a date (with a UK time), a number of days after the place is secured, or not set yet
export function dueFromRow(row) {
  if (row?.default_due_at) {
    const u = uk(row.default_due_at)
    return { mode: 'date', day: ukDayOf(row.default_due_at), at: `${pad(u.hh)}:${pad(u.mm)}`, days: '' }
  }
  if (Number.isInteger(row?.default_due_days)) return { mode: 'days', day: '', at: '19:00', days: String(row.default_due_days) }
  return { mode: 'none', day: '', at: '19:00', days: '' }
}

// The 2 columns a form writes (at most 1 is set: price_map_one_due_default), or an error line
export function dueColumns(due) {
  if (due.mode === 'date') {
    if (!due.day) return { error: 'Pick the date the balance is due.' }
    const at = ukToIso(due.day, due.at || '19:00')
    if (!at) return { error: 'Pick the date the balance is due.' }
    return { default_due_at: at, default_due_days: null }
  }
  if (due.mode === 'days') {
    const n = Number(due.days)
    if (!/^\d{1,3}$/.test(String(due.days).trim()) || n > 365) return { error: 'Type a number of days from 0 to 365.' }
    return { default_due_at: null, default_due_days: n }
  }
  return { default_due_at: null, default_due_days: null }
}

// The price ids the screen asks stripe-prices for, at most 50 a call
export function chunks(ids, size = 50) {
  const out = []
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size))
  return out
}

// A Stripe product's name as a price map label: a name, never an amount, with Stripe's dashes
// written as commas (src/admin/ui/format.js, shared with Add a product)
export { labelFromStripeName }

// ---- Mismatches ----------------------------------------------------------------------------------
// Paid, but no access and Access, but no payment are the same jobs Today shows: their words, buttons
// and what each button does are src/admin/mismatches.js's, so the 2 screens agree.

export { MISMATCH_KINDS, paidText, paidActions, unmappedPricesOf, noPaymentText, firstOf, reasonDone } from '../mismatches.js'

export const MISMATCH = {
  heading: 'Mismatches',
  // paid_no_access is raised the moment Stripe's event lands; access_no_payment by the morning check;
  // the prices not on the map are read from Stripe when the page opens
  aside: 'Updated as payments land, and each morning',
  none: 'Nothing to sort out. Every payment has opened what it should, and everyone with access has a payment or a reason.',
  kindPaid: MISMATCH_WORDS.kindPaid,
  kindNoPayment: MISMATCH_WORDS.kindNoPayment,
  kindUnmapped: 'In Stripe, not on the map',
  createMember: MISMATCH_WORDS.create,
  matchSomeone: MISMATCH_WORDS.match,
  mapThis: MISMATCH_WORDS.map,
  handled: MISMATCH_WORDS.handled,
  addReason: MISMATCH_WORDS.addReason,
  revoke: MISMATCH_WORDS.revoke,
  mapIt: 'Map it',
  leaveOut: 'Mark as not in the hub',
  ownerMaps: MISMATCH_WORDS.ownerMaps,
}

// "£274.25+vat a month for "The Funding Engine, Balance (instalment plan)", added in Stripe on 30 Sep."
// The year shows only when it isn't this year, as the boards write a date.
export function unmappedText(p, nowMs = Date.now()) {
  const amount = p.display ? priceLine(p.display, { interval: p.interval }) : 'A price'
  // Stripe's own name, with its dashes, spaced hyphens included, written as commas (Adam's writing rules)
  const clean = labelFromStripeName(p.product_name)
  const name = clean ? ` for "${clean}"` : ''
  const made = p.created_at ? `, added in Stripe on ${ukDateShort(p.created_at, nowMs)}` : ''
  const archived = p.active === false ? '. It\'s archived in Stripe' : ''
  return `${amount}${name}${made}${archived}. A payment on it opens nothing, and shows here to sort out.`
}

// The words a ticked-off job shows: its resolution, and who did it
// (the same line Today shows beside a ticked job)
export function doneText(job) {
  return doneStamp(job)
}

// ---- How payments change access: the board's 7 lines ------------------------------------------

export const RULES = [
  { when: 'Someone pays', then: 'Access opens as soon as Stripe confirms, and the access email goes from course@adamslee.co.uk.' },
  { when: 'Someone secures a place', then: 'Any product. They can log in, and it shows as place secured, with 1 button to pay the balance. Nothing in it opens until the balance is paid.' },
  { when: 'A payment fails', then: 'The member sees a banner and everything stays open for 7 days. Then the product pauses, and it opens again the moment the payment goes through.' },
  { when: 'You pause a subscription', then: "The product is off while it's paused, and opens again on the resume date. The pause reason from Stripe shows on the person's page." },
  { when: 'You refund', then: "You're asked whether to end their access, because some refunds are goodwill. Nothing ends on its own, and progress is kept either way." },
  { when: 'Paid by bank transfer', then: 'Grant it by hand with the reason, so it never shows as a missing payment.' },
  { when: 'You grant it free', then: "A reason is needed, and it's logged on the person's history." },
]
